import { useEffect } from 'react';

import { useStore } from '../store';
import { IconCheck, IconClose } from './icons';
import { cx, useSheetOpen } from './index';

function Toast({
  id,
  message,
  tone,
  ttlMs,
}: {
  id: number;
  message: string;
  tone: string;
  ttlMs: number;
}) {
  const dismiss = useStore((s) => s.dismissToast);
  useEffect(() => {
    const timer = setTimeout(() => dismiss(id), ttlMs);
    return () => clearTimeout(timer);
  }, [id, ttlMs, dismiss]);

  return (
    <div
      // UX-445: a refusal is announced as an alert, not politely alongside the successes.
      role={tone === 'error' ? 'alert' : 'status'}
      data-testid="toast"
      data-tone={tone}
      className={cx(
        'pointer-events-auto flex items-center gap-2 rounded-[14px] px-4 py-3 text-sm shadow-lg',
        tone === 'error' ? 'bg-surface-2 text-ink' : 'bg-surface-2 text-ink',
      )}
    >
      <span className={tone === 'error' ? 'text-pink' : 'text-cyan'}>
        {tone === 'error' ? <IconClose size={18} /> : <IconCheck size={18} />}
      </span>
      <span className="flex-1">{message}</span>
    </div>
  );
}

/**
 * Toast host (Figma M-C6 50:1213): a stack just above the tab bar. Every message the app
 * shows goes through the store, so a screen never renders its own banner.
 *
 * While a sheet is open the stack moves to the **top** of the screen instead: the host sits
 * above the sheet (z-60 vs z-50) and its resting place is exactly where the sheet puts its
 * actions, so "Agregada a la fila" used to cover "Guardar cambios" and "Quitar de la fila"
 * (QA3 MOB-119). It still floats over the backdrop, so the message stays readable.
 */
export function Toasts() {
  const toasts = useStore((s) => s.toasts);
  const sheetOpen = useSheetOpen();
  if (toasts.length === 0) return null;
  return (
    <div
      data-testid="toast-host"
      data-placement={sheetOpen ? 'top' : 'bottom'}
      className={cx(
        'pointer-events-none fixed inset-x-0 z-60 flex flex-col gap-2 px-4',
        sheetOpen
          ? 'top-[calc(env(safe-area-inset-top)+0.75rem)]'
          : 'bottom-[calc(var(--spacing-tabbar)+0.75rem)]',
      )}
    >
      {toasts.map((t) => (
        <Toast key={t.id} id={t.id} message={t.message} tone={t.tone} ttlMs={t.ttlMs} />
      ))}
    </div>
  );
}
